import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';

import { jwtDecode } from 'jwt-decode';

import { DEFAULT_ACCESS_TOKEN_ROUTE } from '../../shared/constants';
import { TokenContext, TokenContextValue } from '../contexts/token-context';

interface Props extends PropsWithChildren {
   defaultToken?: string;
   tokenUrl?: string;
}

async function getToken(tokenUrl: string) {
   try {
      const response = await fetch(tokenUrl);
      if (!response.ok) {
         return undefined;
      }

      const data = await response.json();
      return data.token as string;
   } catch (_) {
      return undefined;
   }
}

function getExpiresAtFromToken(token: string) {
   try {
      const decoded = jwtDecode(token);
      if (!decoded.exp) return undefined;
      return decoded.exp * 1000;
   } catch (_) {
      return undefined;
   }
}

export const TokenProvider = ({ tokenUrl = DEFAULT_ACCESS_TOKEN_ROUTE, defaultToken, children }: Props) => {
   const [accessToken, setAccessToken] = useState(defaultToken);
   const [expiresAt, setExpiresAt] = useState<number | undefined>(() => (defaultToken ? getExpiresAtFromToken(defaultToken) : undefined));
   const [isLoading, setIsLoading] = useState(!defaultToken);

   const fetchToken = useCallback(async () => {
      const token = await getToken(tokenUrl);
      if (!token) {
         setAccessToken(undefined);
         setExpiresAt(undefined);
         return;
      }

      setAccessToken(token);
      setExpiresAt(getExpiresAtFromToken(token));
   }, [tokenUrl]);

   const refreshToken = useCallback(async () => {
      const response = await fetch(`${tokenUrl}/refresh`, {
         method: 'POST'
      });
      if (!response.ok) return false;
      const data = await response.json();
      const token = data.token as string;
      setAccessToken(token);
      setExpiresAt(getExpiresAtFromToken(token));
      return true;
   }, [tokenUrl]);

   useEffect(() => {
      const init = async () => {
         if (accessToken) return;
         await fetchToken();
         setIsLoading(false);
      };

      init();
   }, [accessToken, fetchToken]);

   useEffect(() => {
      if (!expiresAt) return undefined;

      const timeUntilRefresh = expiresAt - Date.now();
      if (timeUntilRefresh <= 0) {
         fetchToken();
         return undefined;
      }

      const timerId = setTimeout(fetchToken, timeUntilRefresh);
      return () => clearTimeout(timerId);
   }, [expiresAt, fetchToken]);

   const tokenContextValue = useMemo<TokenContextValue>(
      () => ({
         accessToken,
         isLoading,
         refreshToken
      }),
      [accessToken, isLoading, refreshToken]
   );

   return <TokenContext value={tokenContextValue}>{children}</TokenContext>;
};
